import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api, setToken, type ClientUser } from "@/lib/api";

interface UserStore {
  user: ClientUser | null;
  token: string | null;
  error: string | null;
  loading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      error: null,
      loading: false,

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const res = await api.login(email, password);
          setToken(res.token);
          set({ user: res.user, token: res.token });
        } catch (e) {
          set({ error: e instanceof Error ? e.message : "Login failed" });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      logout: () => {
        setToken(null);
        set({ user: null, token: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: "user-store",
      partialize: (state) => ({ user: state.user, token: state.token }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          setToken(state.token);
        }
      },
    },
  ),
);

/** SSR-safe hook — returns false on server and until persist rehydration completes. */
export function useHydrated() {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const unsub = useUserStore.persist.onFinishHydration(() => setHydrated(true));
    if (useUserStore.persist.hasHydrated()) setHydrated(true);
    return unsub;
  }, []);
  return hydrated;
}
